<!--
  AvailabilityWeek: week strip of availabilities (one tile per weekday with its time ranges, weekend only when used) –
  the same look on every profile (acquisition item, applicant, sales partner cockpit, Schüler:in, …).
  Props: availabilities ({ dayOfWeek, timeRange: { startTime, endTime } (minutes), labels? }[], already filtered),
  tones (optional { [dayOfWeek]: 'hit' | 'miss' | 'part' }: colours single days, e.g. fitting / not fitting days in the near views),
  emptyText (optional text of a day without ranges, default "–"), hint (optional text appended to every range tooltip)
-->
<template>
  <div class="aw">
    <div v-for="day in days" :key="day.value" :class="[{ 'aw-day--off': !day.ranges.length }, toneClass(day.value)]" class="aw-day">
      <span class="aw-day__name">{{ day.short }}</span>
      <span v-if="!day.ranges.length" class="aw-day__time">{{ emptyText }}</span>
      <span v-for="(range, i) in day.ranges" :key="i" :title="hint ? `${range.labels} · ${hint}` : range.labels" class="aw-day__time">{{ range.text }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import moment from 'moment';
import {Weekdays} from '@/helpers/dateHelpers/Weekdays.enum';
import {AvailabilityLabelsVue} from '@/enums/AvailabilityLabels';

export interface WeekAvailability {
  dayOfWeek: number;
  timeRange: { startTime: number; endTime: number };
  labels?: string[];
}

export interface WeekDay {
  value: number;
  short: string;
  ranges: Array<{ text: string; labels: string }>;
}

const toTime = (minutes: number) => moment().startOf('day').add(minutes, 'minutes').format('HH:mm');

/** Weekdays Mo–Fr always, Sa/So only when there is a range */
export function weekDaysOf(availabilities: WeekAvailability[]): WeekDay[] {
  const items = Weekdays.Items.map((day: { value: number; text: string }) => ({
    value: day.value,
    short: day.text.slice(0, 2),
    ranges: availabilities
        .filter((av) => av.dayOfWeek === day.value && av.timeRange)
        .sort((a, b) => a.timeRange.startTime - b.timeRange.startTime)
        .map((av) => ({
          text: `${toTime(av.timeRange.startTime)}–${toTime(av.timeRange.endTime)}`,
          labels: (av.labels || []).map((label) => AvailabilityLabelsVue.find((el) => el.value === label)?.text || 'Unbekannt').join(', '),
        })),
  }));
  return items.filter((day: WeekDay) => day.ranges.length || day.value < Weekdays.SATURDAY);
}

export default Vue.extend({
  props: {
    availabilities: {type: Array as () => WeekAvailability[], required: true},
    tones: {type: Object as () => Record<number, 'hit' | 'miss' | 'part'>, default: undefined},
    emptyText: {type: String, default: '–'},
    hint: {type: String, default: ''},
  },
  methods: {
    toneClass(day: number): string {
      const tone = this.tones ? this.tones[day] : undefined;
      return tone ? `aw-day--${tone}` : '';
    },
  },
  computed: {
    days(): WeekDay[] {
      return weekDaysOf(this.availabilities);
    },
  },
});
</script>

<style scoped>
.aw {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 4px;
  margin-top: 4px;
}

.aw-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 6px 4px;
  border-radius: 8px;
  background: rgba(46, 125, 50, .08);
}

.aw-day--off {
  background: rgba(15, 23, 42, .04);
}

.aw-day__name {
  font-size: 11px;
  font-weight: 600;
  color: rgba(15, 23, 42, .6);
}

.aw-day__time {
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: rgba(15, 23, 42, .85);
  white-space: nowrap;
}

.aw-day--off .aw-day__time {
  color: rgba(15, 23, 42, .35);
}

.aw-day.aw-day--hit {
  background: #E7F6EC;
}

.aw-day.aw-day--miss {
  background: #FDECEC;
}

.aw-day.aw-day--part {
  background: #FFF4DB;
}

.aw-day--hit .aw-day__name, .aw-day--hit .aw-day__time {
  color: #1B5E20;
}

.aw-day--miss .aw-day__name, .aw-day--miss .aw-day__time {
  color: #B71C1C;
}

.aw-day--part .aw-day__name, .aw-day--part .aw-day__time {
  color: #7A4F00;
}

.theme--dark .aw-day {
  background: rgba(129, 199, 132, .12);
}

.theme--dark .aw-day--off {
  background: rgba(255, 255, 255, .06);
}

.theme--dark .aw-day__name {
  color: rgba(255, 255, 255, .6);
}

.theme--dark .aw-day__time {
  color: rgba(255, 255, 255, .85);
}

.theme--dark .aw-day--off .aw-day__time {
  color: rgba(255, 255, 255, .35);
}
.theme--dark .aw-day.aw-day--hit {
  background: rgba(102, 187, 106, .16);
}

.theme--dark .aw-day.aw-day--miss {
  background: rgba(239, 83, 80, .16);
}

.theme--dark .aw-day.aw-day--part {
  background: rgba(255, 183, 77, .16);
}

.theme--dark .aw-day--hit .aw-day__name, .theme--dark .aw-day--hit .aw-day__time {
  color: #A5D6A7;
}

.theme--dark .aw-day--miss .aw-day__name, .theme--dark .aw-day--miss .aw-day__time {
  color: #EF9A9A;
}

.theme--dark .aw-day--part .aw-day__name, .theme--dark .aw-day--part .aw-day__time {
  color: #FFD180;
}
</style>
